<template>
  <LoadingTable
    v-if="isLoading && !displaySkeleton"
    class="relative h-[calc(100vh-505px)] overflow-y-auto rounded-md"
  />
  <EquipmentSkeletonTable v-else-if="displaySkeleton && isLoading" />
  <div
    v-else
    class="relative max-h-[calc(100vh-320px)] overflow-y-auto rounded-md"
    style="border: 1px solid rgba(31, 36, 46, 0.14)"
  >
    <div
      class="sticky top-0 z-20 grid grid-cols-24 bg-steel-100 text-left text-sm text-gray-700"
      style="border-bottom: 1px solid rgba(31, 36, 46, 0.14)"
    >
      <span class="col-span-1 p-2">Verified</span>
      <span class="col-span-2 py-2 pl-4 pr-2">Truck #</span>
      <span class="col-span-4 p-2">VIN</span>
      <span class="col-span-1 p-2">Year</span>
      <span class="col-span-2 p-2">Make</span>
      <span class="col-span-4 p-2">Model</span>
      <span class="col-span-3 p-2">COI</span>
      <span class="col-span-3 p-2">ELD</span>
      <span class="col-span-2 p-2">Source</span>
      <span class="col-span-2 p-2">Last Seen</span>
    </div>
    <div
      class="overflow-y-auto"
      :class="[
        showMoreRows ? 'h-[calc(100vh-550px)]' : 'h-[calc(100vh-650px)]',
      ]"
    >
      <div
        v-for="(carrier, index) in data"
        :key="carrier.id"
        :class="[
          'hover:bg-info-subtle-bg grid grid-cols-24 items-center whitespace-nowrap border-b text-left text-sm font-normal text-gray-900',
          showDetail && selectedCarrier === carrier.id
            ? 'bg-alert-closed-border'
            : '',
        ]"
        :style="
          index !== data.length - 1
            ? { borderColor: 'rgba(31, 36, 46, 0.14)' }
            : {}
        "
        @click="onClick(carrier)"
      >
        <span class="col-span-1 py-2 pl-5 pr-1 font-normal text-gray-900">
          <img
            v-if="carrier.verified"
            src="@/assets/verified-icon.svg"
            alt="Verified Icon"
          />
          <img
            v-else
            src="@/assets/unverified-icon.svg"
            alt="Unverified Icon"
          />
        </span>
        <span class="col-span-2 truncate py-2 pl-4 pr-2 font-normal">{{
          carrier.truck_number
        }}</span>
        <span class="col-span-4 grid grid-cols-6 items-center p-2 font-normal">
          <span class="col-span-4 truncate">{{ carrier.vin }}</span>
          <span class="col-span-2 flex items-center justify-start">
            <Popper
              :show="popperStatus[index]?.isVisible"
              placement="top"
              :arrow="true"
              offsetDistance="6"
            >
              <div
                @mouseenter="togglePopper(index, true)"
                @mouseleave="togglePopper(index, false)"
                @click="copyVinToClipboard(carrier.vin, index)"
                class="cursor-pointer hover:bg-light-gray active:opacity-70"
                style="padding: 2px; border-radius: 3px"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13.125 14.375V17.1875C13.125 17.705 12.705 18.125 12.1875 18.125H4.0625C3.81386 18.125 3.5754 18.0262 3.39959 17.8504C3.22377 17.6746 3.125 17.4361 3.125 17.1875V6.5625C3.125 6.045 3.545 5.625 4.0625 5.625H5.625C6.04381 5.62472 6.46192 5.65928 6.875 5.72834M13.125 14.375H15.9375C16.455 14.375 16.875 13.955 16.875 13.4375V9.375C16.875 5.65834 14.1725 2.57417 10.625 1.97834C10.2119 1.90928 9.79381 1.87472 9.375 1.875H7.8125C7.295 1.875 6.875 2.295 6.875 2.8125V5.72834M13.125 14.375H7.8125C7.56386 14.375 7.3254 14.2762 7.14959 14.1004C6.97377 13.9246 6.875 13.6861 6.875 13.4375V5.72834M16.875 11.25V9.6875C16.875 8.94158 16.5787 8.22621 16.0512 7.69876C15.5238 7.17132 14.8084 6.875 14.0625 6.875H12.8125C12.5639 6.875 12.3254 6.77623 12.1496 6.60041C11.9738 6.4246 11.875 6.18614 11.875 5.9375V4.6875C11.875 4.31816 11.8023 3.95243 11.6609 3.6112C11.5196 3.26998 11.3124 2.95993 11.0512 2.69876C10.7901 2.4376 10.48 2.23043 10.1388 2.08909C9.79757 1.94775 9.43184 1.875 9.0625 1.875H8.125"
                    stroke="#6C7C9D"
                    stroke-width="1.25"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <template #content>
                <div
                  class="w-auto whitespace-nowrap rounded-md bg-dark-gray px-2 py-1 text-center text-2xs text-white"
                >
                  {{ popperStatus[index]?.message }}
                </div>
              </template>
            </Popper>
          </span>
        </span>
        <span class="col-span-1 p-2 font-normal">{{ carrier.year }}</span>
        <span class="col-span-2 truncate p-2 font-normal">{{
          carrier.make
        }}</span>
        <span class="col-span-4 truncate p-2 font-normal">{{
          carrier.model
        }}</span>
        <span class="col-span-3 flex gap-1 p-2 font-normal">
          <img
            v-if="carrier.is_coi"
            src="@/assets/insured-icon.svg"
            alt="Insured Icon"
          />
          <img
            v-else
            src="@/assets/coi-not-found-icon.svg"
            alt="COI Not Found Icon"
          />
          <p>{{ carrier.is_coi ? 'Insured' : 'Not Found' }}</p>
        </span>
        <span class="col-span-3 flex gap-1 p-2 font-normal">
          <img
            v-if="carrier.is_eld_and_active"
            src="@/assets/eld-connected-icon.svg"
            alt="ELD Connected Icon"
          />
          <img
            v-else
            src="@/assets/eld-not-connected-icon.svg"
            alt="ELD Not Connected"
          />
          <p>{{ carrier.is_eld_and_active ? 'Connected' : 'Not Connected' }}</p>
        </span>
        <span class="col-span-2 truncate p-2 font-normal">{{
          carrier.source
        }}</span>
        <span class="col-span-2 p-2 font-normal">{{
          dayjs(carrier.last_seen).format('MM/DD/YYYY')
        }}</span>
      </div>
    </div>

    <!-- Sticky Footer -->
    <div
      class="topShadow sticky bottom-0 z-20 flex h-14 items-center justify-center border-t bg-white px-3 py-2 text-sm font-normal text-gray-900"
    >
      <pagination
        :selected-page="pagination.currentPage"
        :total-pages="pagination.totalPages"
        equivalent
        @select-page="onSelectPage"
      ></pagination>
    </div>
  </div>
</template>
<script setup>
import { useStore } from 'vuex';
import { ref, defineProps, defineEmits, computed } from 'vue';
import Pagination from '@/components/LightPagination.vue';
import LoadingTable from '@/components/LoadingTable.vue';
import EquipmentSkeletonTable from '@/components/EquipmentSkeletonTable.vue';
import Popper from 'vue3-popper';
import dayjs from 'dayjs';

defineProps({
  data: {
    type: Array,
    required: true,
  },
  pagination: {
    type: Object,
    required: true,
  },
  showDetail: {
    type: Boolean,
    default: false,
  },
  selectedCarrier: { type: [Number, String, null], default: null },
  displaySkeleton: {
    type: Boolean,
    default: false,
  },
  showMoreRows: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const emit = defineEmits(['click']);

const popperStatus = ref({});
const isLoading = ref(false);
const carrierId = computed(
  () =>
    store.state.users.currentUser?.requested_carrier_id ||
    store.state.users.currentUser?.company?.carrier_id
);

const togglePopper = (key, isVisible) => {
  if (!popperStatus.value[key]) {
    popperStatus.value[key] = { isVisible: false, message: 'Copy' };
  }
  popperStatus.value[key].isVisible = isVisible;
};

const copyVinToClipboard = async (vin, key) => {
  try {
    await navigator.clipboard.writeText(vin);
    popperStatus.value[key].message = 'Copied!';
    togglePopper(key, true);
    setTimeout(() => {
      togglePopper(key, false);
      popperStatus.value[key].message = 'Copy';
    }, 1500);
  } catch (err) {
    console.error('Cant save the VIN on the Clipboard:', err);
  }
};

const onSelectPage = async (page) => {
  isLoading.value = true;
  await store.dispatch('carriers/getCarrierVehiclesForCarrier', {
    carrierId: carrierId.value,
    page,
  });
  isLoading.value = false;
};

const onClick = (carrier) => {
  emit('click', carrier);
};
</script>
<style scoped>
.topShadow {
  box-shadow: 0px -1px 6px 0px rgba(160, 160, 160, 0.22);
}
</style>
